import type { ApiFindingItem, ApiMetadataItem } from './types';
import { getSecurityFindings } from './api';

export function escapeMarkdownCell(value: string | number | null | undefined): string {
  if (value == null) return "";
  return String(value).replace(/\|/g, "/").replace(/\n/g, " ").trim();
}

export function normalizeMetadataSearchCandidates(fileName: string): string[] {
  const trimmed = fileName.trim();
  if (!trimmed) return [];
  const withoutExt = trimmed.replace(/\.(cls|trigger|js|ts|jsx|tsx|html|xml|cmp|app)$/i, "");
  return Array.from(new Set([trimmed, withoutExt].filter(Boolean)));
}

export function buildSecurityFindingsMarkdownTable(findings: ApiFindingItem[]): string {
  const rows = findings
    .map(
      (finding) =>
        `| ${escapeMarkdownCell(finding.severity?.toUpperCase() || "INFO")} | ${escapeMarkdownCell(finding.line)} | ${escapeMarkdownCell(finding.rule)} | ${escapeMarkdownCell(finding.message)} |`,
    )
    .join("\n");

  return `| Severity | Line | Rule | Issue |\n|---|---|---|---|\n${rows}`;
}

export function buildImproveWithAIPromptForMetadataItem(
  item: Pick<ApiMetadataItem, "file_name" | "findings">,
): string {
  const findingsTable = buildSecurityFindingsMarkdownTable(item.findings);
  return `Fix ${item.findings.length} security issues in ${item.file_name}:\n\n${findingsTable}\n\nFix all issues. Return ONE complete code block.`;
}

export async function getSecurityFindingsForMetadataItem(params: {
  org_id: number | string;
  user_id: number | string;
  metadata_item_id: string;
  file_name: string;
  metadata_type?: string;
}): Promise<ApiMetadataItem | null> {
  const searchCandidates = normalizeMetadataSearchCandidates(params.file_name);

  for (const search of searchCandidates) {
    const response = await getSecurityFindings({
      org_id: params.org_id,
      user_id: params.user_id,
      metadata_type: params.metadata_type,
      search,
      page: 1,
      pageSize: 200,
    });

    const metadataItems = response.metadataItems ?? [];
    const exact = metadataItems.find(
      (item) =>
        item.metadata_item_id === params.metadata_item_id &&
        (!params.metadata_type || item.metadata_type === params.metadata_type),
    );
    if (exact) return exact;

    const sameFile = metadataItems.find(
      (item) =>
        item.file_name === params.file_name &&
        (!params.metadata_type || item.metadata_type === params.metadata_type),
    );
    if (sameFile) return sameFile;
  }

  return null;
}

/**
 * Get security health summary
 * GET /security/health?org_id=xxx&user_id=xxx
 *
 * @param params Query parameters
 * @returns Security health data
 */
